import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LabsAppBar, { ActionButton } from '../LabsAppBar';
import { UnifiedSchema } from '../../types/schema';

interface SchemaEditorLayoutProps {
  breadcrumbs: { name: string; iconName?: string; path?: string }[];
  actions: ActionButton[];
  currentSchema: UnifiedSchema | null;
  onSchemaRenameConfirm: (newName: string, newDescription: string) => Promise<void>;
  isLoading: boolean;
  children: React.ReactNode;
  onExport?: (action: 'playbook-yaml' | 'connection-yaml' | 'combined-gz' | 'combined-yaml') => void;
}

const SchemaEditorLayout: React.FC<SchemaEditorLayoutProps> = ({
  breadcrumbs,
  actions,
  currentSchema,
  onSchemaRenameConfirm,
  isLoading,
  children,
  onExport,
}) => {
  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <LabsAppBar
        breadcrumbs={breadcrumbs}
        actions={actions}
        currentSchema={currentSchema}
        onSchemaRenameConfirm={onSchemaRenameConfirm}
        onExport={onExport}
      />

      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 flex items-center justify-center bg-gray-100/75 dark:bg-gray-900/75 backdrop-blur-sm z-100"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.2, delay: 0.1 }}
              className="flex flex-col items-center p-8 rounded-lg shadow-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
            >
              <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
              <div className="text-gray-600 dark:text-gray-400 text-xl font-semibold">Loading Schema...</div>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">Please wait while we fetch the definitions.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col md:flex-row min-h-0">
        {children}
      </div>
    </div>
  );
};

export default SchemaEditorLayout;
